/**
 * aC Men's Hub の売上集計（定義の版つき台帳）の API 契約（zod schema + 型 + 画面の語彙）。
 *
 * 毎朝の計算ジョブ（apps/generator/ac-mens-hub-daily）は BigQuery の台帳
 * `ac_mens_hub.revenue__ledger` に仕様 SQL の出力をそのまま書く。API（apps/api/ac-mens-hub）は
 * それを画面の勘定・プラン・内訳（このファイルの語彙）に読み替えて Web（apps/web/ac-mens-hub）へ返す。
 * 読み替えの対応は API の `features/revenue/domain/ledger-reading.ts` が1か所で持ち、
 * API と Web はこのファイルの語彙と形を共有する。
 * 要対応の分類・重大度・合否は台帳の検算・検知の記録 `revenue__checks` に計算ジョブが書く値と同じ。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計の画面の語彙と、台帳・版の比較・締め・要対応・定義の API 契約を API と Web で共有する。
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 月を締める・締め直すための権限。
 *
 * 売上ページを見る権限（`revenue`）とは別に配る。締めは「どの朝の計算を確定値にするか」を
 * 記録する操作で、見られる人全員が押せると確定値が経理の確認なしに動く。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上の月を締める・締め直す操作に必要な権限を表す。閲覧権限とは独立に配る。
 * @graph-connects none
 */
export const REVENUE_CLOSE_PERMISSION = 'revenue-close';
/**
 * 予実画面の売上実績を v0.1 の売上高に切り替える日（JST、この日を含む）。
 * これより前の日は旧Hub（v0）の売上実績のまま出す。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 予実の売上実績の出どころを旧Hubから正式版の売上高へ切り替える日を1か所で持つ。
 * @graph-connects none
 */
export const REVENUE_BUDGET_SWITCH_DATE = '2026-10-01';
/**
 * 正式版（v0.1）の台帳を書く毎朝の計算の実行時刻（JST）。画面の「毎朝いつ更新されるか」の表示に使う。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 正式版の台帳が毎朝更新される時刻を1か所で持ち、画面の案内に使う。
 * @graph-connects none
 */
export const REVENUE_LEDGER_RUN_TIME_JST = '05:50';
/**
 * 画面で選べる定義の版。v0.1 が正式版、v0 は旧Hub（会計定義と異なる）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上を表示できる定義の版の一覧。正式版と旧Hubを区別する。
 * @graph-connects none
 */
export const RevenueLedgerVersionSchema = z.enum(['v0.1', 'v0']);
/**
 * 正式版。既定の表示と予実はこの版を使う。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 既定表示と予実に使う正式な定義の版を表す。
 * @graph-connects none
 */
export const REVENUE_OFFICIAL_VERSION = 'v0.1';
/**
 * 締められる版。v0 は毎朝の計算（run）を持たないため締めの対象にしない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 月を締められる定義の版を限定する。旧Hubは締めの対象外。
 * @graph-connects none
 */
export const RevenueClosableVersionSchema = z.enum(['v0.1']);
/**
 * 画面の勘定。
 * - `sales` 売上高（会費・違約金・買取、取消はマイナスの行）
 * - `non_operating_income` 営業外収益（失効）
 * - `deferred_revenue` 前受金（月末残高）
 * - `bad_debt` 貸倒（月末残高と月次の増加・減少）
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 台帳の行を読み替えた先の勘定を表す語彙。API と画面で共有する。
 * @graph-connects none
 */
export const RevenueLedgerBucketSchema = z.enum([
    'sales',
    'non_operating_income',
    'deferred_revenue',
    'bad_debt',
]);
/**
 * 画面のプランの区分。1ヶ月に1回（MER）・2ヶ月に1回（BMR / BBD）と、違約金・買取のように全プランを対象にする `all`。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 台帳の行がどのプランの数字かを表す語彙。
 * @graph-connects none
 */
export const RevenueLedgerPlanSchema = z.enum(['monthly', 'bimonthly', 'all']);
/**
 * 画面の内訳。取消（`cancel_*`）の金額はマイナス。貸倒の `increase` / `decrease` は
 * その月の増加額・減少額をプラスの大きさで持ち、`balance` は基準日時点の残高を持つ。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 画面の内訳を表す語彙。取消はマイナス、貸倒の増減はプラスの大きさで持つ約束を含む。
 * @graph-connects none
 */
export const RevenueLedgerBreakdownSchema = z.enum([
    'membership_ticket',
    'purchased_ticket',
    'membership_fee',
    'penalty',
    'buyout',
    'cancel_membership_fee',
    'cancel_penalty',
    'cancel_buyout',
    'expiry_due',
    'expiry_withdrawal',
    'balance',
    'increase',
    'decrease',
]);
/**
 * v0（旧Hub）の行の勘定。VIEW `revenue__ledger_v0` の列名（v0_column）を内訳に入れ、勘定は `v0` を1つだけ置く。
 * v0 の列は勘定の分け方が v0.1 と違うため、v0.1 の勘定と混ぜない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 旧Hubの売上を縦長に並べた行の勘定を表す。v0.1 の勘定と混ざらないよう専用の値にする。
 * @graph-connects none
 */
export const REVENUE_V0_LEDGER_BUCKET = 'v0';
/**
 * v0 の行の内訳（`revenue__daily` の列名そのまま。VIEW の v0_column）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 旧Hubの13指標の列名を、縦長に並べたときの内訳として表す。
 * @graph-connects none
 */
export const RevenueV0ColumnSchema = z.enum([
    'subscription_fee_monthly',
    'subscription_fee_bimonthly',
    'ticket_purchase_revenue',
    'penalty_fee',
    'item_purchase',
    'expired_revenue',
    'bad_debt',
    'bad_debt_recovery',
    'refund',
    'deferred_inflow_subscription',
    'deferred_inflow_ticket',
    'deferred_balance_subscription',
    'deferred_balance_ticket',
]);
/**
 * v0 の列のうち残高（月末の値）の列。VIEW はこの列だけ月の最後の日の値を持つ。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 旧Hubの13指標のうち、足し算せず時点の値として読む前受金残高の列を表す。
 * @graph-connects none
 */
export const REVENUE_V0_BALANCE_COLUMNS = [
    'deferred_balance_subscription',
    'deferred_balance_ticket',
];
/**
 * 正式版の売上高に入る内訳（会費＋買取＋違約金−取消）。営業外収益（失効）は入らない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上高の合計（予実の売上実績・締めの確認・版の比較・売上ページのチャートの合計）に数える内訳を1か所で持つ。
 * @graph-connects none
 */
export const REVENUE_SALES_BREAKDOWNS = [
    'membership_ticket',
    'purchased_ticket',
    'membership_fee',
    'penalty',
    'buyout',
    'cancel_membership_fee',
    'cancel_penalty',
    'cancel_buyout',
];
/**
 * 勘定の表示名。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 勘定の語彙を画面に出す日本語名へ対応づける。
 * @graph-connects none
 */
export const REVENUE_LEDGER_BUCKET_LABELS = {
    sales: '売上高',
    non_operating_income: '営業外収益',
    deferred_revenue: '前受金',
    bad_debt: '貸倒',
};
/**
 * プランの表示名。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business プランの語彙を画面に出す日本語名へ対応づける。
 * @graph-connects none
 */
export const REVENUE_LEDGER_PLAN_LABELS = {
    monthly: '1ヶ月に1回',
    bimonthly: '2ヶ月に1回',
    all: '全プラン',
};
/**
 * 内訳の表示名。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 内訳の語彙を画面に出す日本語名へ対応づける。
 * @graph-connects none
 */
export const REVENUE_LEDGER_BREAKDOWN_LABELS = {
    membership_ticket: '会費チケット',
    purchased_ticket: '購入チケット',
    membership_fee: '会費',
    penalty: '違約金',
    buyout: '買取',
    cancel_membership_fee: '取消（会費）',
    cancel_penalty: '取消（違約金）',
    cancel_buyout: '取消（買取）',
    expiry_due: '期日失効',
    expiry_withdrawal: '退会失効',
    balance: '残高',
    increase: '増加',
    decrease: '減少',
};
/**
 * 残高として読む行かを判定する。正式版は前受金の全行と貸倒の残高行、v0 は前受金残高の列が残高で、
 * それ以外は流れ（期間の合計）。残高は足し算できないため、集め方は `pickRevenueBalanceAt` に従う。
 *
 * @param bucket 勘定（v0 は `v0`）
 * @param breakdown 内訳（v0 は旧列名）
 * @returns 残高なら true
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 台帳の行が残高か流れかを判定する唯一の条件。月や期間への集め方を決める。
 * @graph-connects REVENUE_V0_BALANCE_COLUMNS [references] v0 の残高の列
 */
export function isRevenueLedgerBalance(bucket, breakdown) {
    if (bucket === REVENUE_V0_LEDGER_BUCKET) {
        return REVENUE_V0_BALANCE_COLUMNS.some((column) => column === breakdown);
    }
    if (bucket === 'deferred_revenue')
        return true;
    return bucket === 'bad_debt' && breakdown === 'balance';
}
/**
 * 売上高の合計に数える行かを判定する（勘定が売上高で、内訳が REVENUE_SALES_BREAKDOWNS のもの）。
 *
 * @param bucket 勘定
 * @param breakdown 内訳
 * @returns 売上高に入るなら true
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上高の合計に数える行を判定する唯一の条件。締め・予実・版の比較・売上ページのチャートの合計が同じ条件で数える。
 * @graph-connects REVENUE_SALES_BREAKDOWNS [references] 売上高に入る内訳
 */
export function isRevenueSalesAmount(bucket, breakdown) {
    return bucket === 'sales' && REVENUE_SALES_BREAKDOWNS.some((b) => b === breakdown);
}
/**
 * 残高の値として採る行を選ぶ。残高は「期間の最後の月の、最も新しい基準日」の値を採る
 * （月の値ならその月、期間の値なら期間末の月。基準日を持たない行は基準日の空の行として比べる）。
 * 期間の最後の月に行が無ければ空（データなし。前の月の値で埋めない）。
 *
 * @param amounts 残高の行（計上月と基準日を持つ）
 * @param periodEndMonth 期間の最後の月（YYYY-MM）
 * @returns 採る行
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 足し算できない残高を、月・期間の値として採る時点を1か所で決める。
 * @graph-connects none
 */
export function pickRevenueBalanceAt(amounts, periodEndMonth) {
    const inMonth = amounts.filter((a) => a.month === periodEndMonth);
    const latest = inMonth.reduce((max, a) => ((a.asOfDate ?? '') > max ? (a.asOfDate ?? '') : max), '');
    return inMonth.filter((a) => (a.asOfDate ?? '') === latest);
}
/**
 * 月（YYYY-MM）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 計上月を YYYY-MM の形で受け取る入力規則。
 * @graph-connects none
 */
export const RevenueMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
/**
 * 日付（YYYY-MM-DD）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 日付を YYYY-MM-DD の形で受け取る入力規則。
 * @graph-connects none
 */
export const RevenueDateSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);
/**
 * 台帳の月次の読み出し条件。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 版と月の範囲を指定して台帳を月次で読む条件。開始月が終了月より後なら受け付けない。
 * @graph-connects none
 */
export const RevenueLedgerMonthlyQuerySchema = z
    .object({
    version: RevenueLedgerVersionSchema,
    fromMonth: RevenueMonthSchema,
    toMonth: RevenueMonthSchema,
})
    .refine((q) => q.fromMonth <= q.toMonth, { message: 'fromMonth must be <= toMonth' });
/**
 * 前受金の状態別の内訳（税抜）。仕様 SQL MER[3] の確定・確定待ち・証跡なし（突合できない）・うち退会済み。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 前受金を確定・確定待ち・証跡なし・うち退会済みに分けた税抜の内訳。
 * @graph-connects none
 */
export const RevenueDeferredStatusSchema = z.object({
    confirmed: z.number().nullable(),
    pending: z.number().nullable(),
    noEvidence: z.number().nullable(),
    withdrawn: z.number().nullable(),
});
/**
 * 台帳の月次の1マス。v0 は税抜の金額しか持たないので、税・税込・件数は null。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 勘定・プラン・内訳の1行のうち、1か月分の金額と前受金の内訳。
 * @graph-connects none
 */
export const RevenueLedgerCellSchema = z.object({
    month: RevenueMonthSchema,
    amountNet: z.number(),
    consumptionTax: z.number().nullable(),
    amountInclTax: z.number().nullable(),
    bookedCount: z.number().nullable(),
    deferredStatus: RevenueDeferredStatusSchema.nullable(),
});
/**
 * 台帳の月次の1行（勘定・プラン・内訳の組）。v0 の行は勘定が `v0`、内訳が旧列名になる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 勘定・プラン・内訳の組ごとに月の金額を並べた台帳の1行。
 * @graph-connects none
 */
export const RevenueLedgerRowSchema = z.object({
    bucket: z.union([RevenueLedgerBucketSchema, z.literal(REVENUE_V0_LEDGER_BUCKET)]),
    planCode: RevenueLedgerPlanSchema,
    breakdown: z.union([RevenueLedgerBreakdownSchema, RevenueV0ColumnSchema]),
    isBalance: z.boolean(),
    cells: z.array(RevenueLedgerCellSchema),
});
/**
 * どの朝の計算か（run）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 毎朝の計算1回を識別する番号・計算時刻・計算の指紋。
 * @graph-connects none
 */
export const RevenueLedgerRunSchema = z.object({
    runId: z.string(),
    computedAt: z.string(),
    logicFingerprint: z.string().nullable(),
});
/**
 * 版の台帳の最新の計算（最終更新）と、その版の台帳の最初の計上月。
 * 期間ではなく版全体から読む（期間に値が無いだけで「まだ実行されていません」と出さず、
 * 「この期間に値はありません（いつから）」と出し分けるため）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 版の台帳で最も新しい計算と、その版の台帳が値を持つ最初の計上月。最終更新と、期間に値が無いときの案内に使う。
 * @graph-connects none
 */
export const RevenueLedgerStatusSchema = RevenueLedgerRunSchema.extend({
    firstMonth: RevenueMonthSchema,
});
/**
 * その月の値を出した計算（締め済み月は締めた計算、それ以外は計上月ごとの最新の計算）。
 * 月によって計算が違うことがある（締め・対象月を指定した流し直しなど）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 計上月ごとに、画面に出した値を出した計算（締め済み月は締めた計算）の番号と時刻。
 * @graph-connects none
 */
export const RevenueLedgerMonthRunSchema = z.object({
    month: RevenueMonthSchema,
    runId: z.string(),
    computedAt: z.string(),
});
/**
 * 月ごとの最新の計算と、その計算の売上高の合計（税抜）。締めの確認でこの計算を確定値にする。
 * closable は今締められるか（月末を過ぎた月だけ true）。判定は API が持ち、画面はこれだけを見る。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 計上月ごとの最新の計算と売上高の合計と、その月を今締められるか。締める・締め直すときの確定値の候補。
 * @graph-connects none
 */
export const RevenueLedgerLatestRunSchema = RevenueLedgerMonthRunSchema.extend({
    salesTotal: z.number(),
    closable: z.boolean(),
});
/**
 * 台帳の月次の応答。版の台帳が空（まだ計算が走っていない・テーブルが無い）なら run は null、rows は空。
 * v0 は台帳に行が無いので版の台帳の最新の計算を読まず、run は null のまま rows には旧Hubの値が入る。
 * 見ている版で締めた月は締めた計算の値、それ以外の月は最新の計算の値を rows に入れる。
 * 当月（月末を過ぎていない月）は、前日を基準日とする最新の計算に当月の行があるときだけ値を出す
 * （月初1日の計算は基準日が前月末なので当月は未計算。日次・予実の当月と同じ数字にそろえる）。
 * そのため値を出さない当月は pendingMonths に入れる（前日を基準日とする計算が書かれるまで出さない。当月の行が1件も無い日も含む）。
 * run は期間に関係なく版の台帳で最も新しい計算と最初の計上月（期間に値が無いだけなら run は null にならない）、
 * monthRuns は月ごとにその月の値を出した計算（締め済み月は締めた計算）、latestRuns は月ごとの最新の計算（締めの対象）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 月×勘定・プラン・内訳の金額（締め済み月は締めた計算の値）と、月ごとの計算を返す応答。
 * @graph-connects none
 */
export const RevenueLedgerMonthlyResponseSchema = z.object({
    version: RevenueLedgerVersionSchema,
    run: RevenueLedgerStatusSchema.nullable(),
    monthRuns: z.array(RevenueLedgerMonthRunSchema),
    latestRuns: z.array(RevenueLedgerLatestRunSchema),
    months: z.array(RevenueMonthSchema),
    pendingMonths: z.array(RevenueMonthSchema),
    rows: z.array(RevenueLedgerRowSchema),
});
/**
 * 日次（前日からの増加）の読み出し条件。正式版だけを対象にする。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 日付の範囲を指定して正式版の日次の増加を読む条件。
 * @graph-connects none
 */
export const RevenueLedgerDailyQuerySchema = z
    .object({ from: RevenueDateSchema, to: RevenueDateSchema })
    .refine((q) => q.from <= q.to, { message: 'from must be <= to' });
/**
 * 日次（前日からの増加）の1行。流れの勘定（売上高・営業外収益）だけを返す。
 * 終わった月の月末日には、その月の月次（締め済みなら締めた値）と日次の合計の差も載る。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business ある日の、当月累計の前日からの増加を勘定・プラン・内訳ごとに表す1行。
 * @graph-connects none
 */
export const RevenueLedgerDailyRowSchema = z.object({
    date: RevenueDateSchema,
    bucket: RevenueLedgerBucketSchema,
    planCode: RevenueLedgerPlanSchema,
    breakdown: RevenueLedgerBreakdownSchema,
    amountNet: z.number(),
    consumptionTax: z.number(),
    amountInclTax: z.number(),
});
/**
 * 日次の応答。
 * - firstDate: 日次が出せる最初の日。日別内訳（台帳が始まる前の日を取引日で数え直した参考値）の最初の日と
 *   台帳の最初の日の早い方（日別内訳が無ければ台帳の最初の日、台帳が空なら null）
 * - referenceThrough: 参考値（日別内訳）で出している最後の日（台帳の最初の日の前日。firstDate からこの日まで）。
 *   この後の日は毎朝の台帳の当月累計の差。日別内訳を使っていなければ null（台帳と日別内訳の版・計算の指紋が
 *   違う・間に日の空きがある・日別内訳を読めなかったときも使わない）。使うかは日別内訳の全期間で決めるので、
 *   firstDate と同じく見ている範囲に関係なく同じ値になる（日別内訳の読み出し・読み替えに失敗したときを除く。
 *   そのときは台帳だけで出す）。参考値の月も、月末日で台帳の月次との差を吸収するので月の合計は月次に一致する
 * - rows: firstDate 以降の日だけ（日ごとの記録が始まる前に終わった月の月次を、月末日の1日分として出さないため）
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 正式版の日次の増加と、日次が出せる最初の日・取引日で数え直した参考値で出す最後の日を返す応答。
 * @graph-connects none
 */
export const RevenueLedgerDailyResponseSchema = z.object({
    firstDate: RevenueDateSchema.nullable(),
    referenceThrough: RevenueDateSchema.nullable(),
    rows: z.array(RevenueLedgerDailyRowSchema),
});
/**
 * 版の比較の条件。同じ版どうしは比べない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 2つの版と月の範囲を指定して、対応表の行ごとに値と差額を出す条件。
 * @graph-connects none
 */
export const RevenueLedgerCompareQuerySchema = z
    .object({
    leftVersion: RevenueLedgerVersionSchema,
    rightVersion: RevenueLedgerVersionSchema,
    fromMonth: RevenueMonthSchema,
    toMonth: RevenueMonthSchema,
})
    .refine((q) => q.leftVersion !== q.rightVersion, {
    message: 'leftVersion and rightVersion must differ',
})
    .refine((q) => q.fromMonth <= q.toMonth, { message: 'fromMonth must be <= toMonth' });
/**
 * 版の比較の1行。label が null の側は「対応なし」で、値も null になる。
 * label があって値が null の側は「データなし」（期間に行が無い・期間末の月に残高の行が無い）。
 * 流れの行は期間の合計、残高の行は期間末の月の値。差額は 右 − 左（どちらかが null なら null）。
 * 値は月次と同じく締め済み月を締めた計算の値で出し、最新の計算と違う側は注記に「締め値で表示・最新との差 ±X円」が付く。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 対応表の1行について、両版の値・差額・注記を並べた比較結果。
 * @graph-connects none
 */
export const RevenueLedgerCompareRowSchema = z.object({
    rowNo: z.number().int(),
    leftLabel: z.string().nullable(),
    rightLabel: z.string().nullable(),
    leftValue: z.number().nullable(),
    rightValue: z.number().nullable(),
    diff: z.number().nullable(),
    isBalance: z.boolean(),
    note: z.string(),
});
/**
 * 版の比較の応答。pendingMonths は、どちらかの版で当月の値をまだ出さない月（月次の応答と同じ判定）で、両版ともその月を除いて比べる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 2つの版を対応表どおりに並べた比較結果と、当月の値をまだ出さない月を返す応答。
 * @graph-connects none
 */
export const RevenueLedgerCompareResponseSchema = z.object({
    leftVersion: RevenueLedgerVersionSchema,
    rightVersion: RevenueLedgerVersionSchema,
    fromMonth: RevenueMonthSchema,
    toMonth: RevenueMonthSchema,
    pendingMonths: z.array(RevenueMonthSchema),
    rows: z.array(RevenueLedgerCompareRowSchema),
});
/**
 * 要対応の分類。`post_close_diff`（締め後の差）は検算の記録ではなく、締めの印と最新の計算から API が出す。
 * それ以外は台帳の検算・検知の記録 `revenue__checks` の `category` に計算ジョブが書く値。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 要対応の5分類（更新の失敗・締め後の差・データの穴・検算・版の承認待ち）を表す語彙。
 * @graph-connects none
 */
export const RevenueCheckCategorySchema = z.enum([
    'update_failure',
    'post_close_diff',
    'data_gap',
    'reconciliation',
    'approval_pending',
]);
/**
 * 要対応の重大度。`critical` は即時対応、`warning` は定期確認、`info` は記録のみ
 * （v0.1 の1ヶ月に1回の貸倒の検算のように、違反しても対応を求めないもの）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 要対応の重大度（即時対応・定期確認・記録のみ）を表す語彙。
 * @graph-connects none
 */
export const RevenueCheckSeveritySchema = z.enum(['critical', 'warning', 'info']);
/**
 * 検算・検知の合否。`fail` の行だけが要対応に並ぶ。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 検算・検知の1件が合格か違反かを表す語彙。
 * @graph-connects none
 */
export const RevenueCheckStatusSchema = z.enum(['pass', 'fail']);
/**
 * 要対応の分類ごとの「何が問題か／推奨アクション／担当」。画面の見出しと説明の唯一の出どころ。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 要対応の分類ごとに、何が問題で誰が何をすればよいかを業務の言葉で持つ。
 * @graph-connects none
 */
export const REVENUE_CHECK_CATEGORY_GUIDE = {
    update_failure: {
        label: '更新の失敗',
        problem: '毎朝の計算が失敗し、台帳に書けなかった',
        action: '失敗理由を見て再実行',
        owner: 'エンジニア',
    },
    post_close_diff: {
        label: '締め後の差',
        problem: '締め済み月の再計算値が、締め値と違う',
        action: '差の内訳を見て、改版候補に載せるか締め直すかを判断',
        owner: '経理',
    },
    data_gap: {
        label: 'データの穴',
        problem: '突合できない課金・金額取得失敗・プラン判定不能などの検知行がある',
        action: '件数と金額を確かめ、改版候補に載せるか判断',
        owner: 'エンジニア→経理',
    },
    reconciliation: {
        label: '検算',
        problem: 'チケット1枚1か所・貸倒の増減＝残高の検算に違反した',
        action: '改版候補 C-15 を参照',
        owner: 'エンジニア',
    },
    approval_pending: {
        label: '版の承認待ち',
        problem: '承認記録のない版が登録されている',
        action: '反映を止める',
        owner: '経理',
    },
};
/**
 * 要対応の1件。同じ checkId が対象月・基準日ごとに複数出る。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 要対応の1件（分類・重大度・件数・前回の件数・金額・対象月・基準日・詳細）。
 * @graph-connects none
 */
export const RevenueCheckItemSchema = z.object({
    checkId: z.string(),
    /** データの穴の増加（V-5）の行のときの元の検知（それ以外は null）。同じ checkId の行を見分ける */
    baseCheckId: z.string().nullable(),
    category: RevenueCheckCategorySchema,
    severity: RevenueCheckSeveritySchema,
    count: z.number().nullable(),
    previousCount: z.number().nullable(),
    amount: z.number().nullable(),
    month: RevenueMonthSchema.nullable(),
    asOfDate: RevenueDateSchema.nullable(),
    detail: z.string().nullable(),
});
/**
 * 要対応の読み出し条件。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 版を指定して要対応を読む条件。
 * @graph-connects none
 */
export const RevenueChecksQuerySchema = z.object({ version: RevenueClosableVersionSchema });
/**
 * 要対応の応答。run は検算・検知を記録した最新の計算（記録が無ければ null）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 最新の計算の検算・検知の違反と締め後の差を、要対応として返す応答。
 * @graph-connects none
 */
export const RevenueChecksResponseSchema = z.object({
    run: z.object({ runId: z.string(), computedAt: z.string() }).nullable(),
    items: z.array(RevenueCheckItemSchema),
});
/**
 * 定義の版1件（版の一覧の7項目＋指紋＋本文）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 定義の版番号・決定日・決定の場・承認者・変更点・適用開始月・遡及の有無と、正本の本文。
 * @graph-connects none
 */
export const RevenueDefinitionSchema = z.object({
    version: z.string(),
    decidedOn: z.string().nullable(),
    decidedBy: z.string().nullable(),
    approver: z.string().nullable(),
    changes: z.string().nullable(),
    effectiveFrom: z.string().nullable(),
    retroactive: z.boolean().nullable(),
    logicFingerprint: z.string().nullable(),
    contentSha256: z.string().nullable(),
    contentMd: z.string().nullable(),
    computedAt: z.string().nullable(),
});
/**
 * 定義の応答。決定日の新しい順（同じ日は版の降順）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 登録されている定義の版の一覧と本文を返す応答。
 * @graph-connects none
 */
export const RevenueDefinitionsResponseSchema = z.object({
    definitions: z.array(RevenueDefinitionSchema),
});
/**
 * 月の締めの状態。
 * - `open` 未締め
 * - `closed` 締め済み（確定値と最新の計算で、全ての勘定・プラン・内訳の税抜・税・税込が同じ）
 * - `closed_with_diff` 締め後に差あり（どれか1マスでも違う）
 * - `version_mismatch` 別の版で締めてある（見ている版では未締め）
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 月の締めの4状態（未締め・締め済み・締め後に差あり・版違い）を表す語彙。
 * @graph-connects none
 */
export const RevenueClosingStateSchema = z.enum([
    'open',
    'closed',
    'closed_with_diff',
    'version_mismatch',
]);
/**
 * 締め後の差の1マス（最新の計算 − 確定値）。税抜・税・税込のどれかが違うマスだけを並べる
 * （同じ勘定の中の付け替えや税だけの変化も、合計で 0 に消えないようにマスごとに持つ）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 締め済み月の再計算で動いたマス（勘定・プラン・内訳）と、その税抜・税・税込の差額。
 * @graph-connects none
 */
export const RevenueClosingDiffSchema = z.object({
    bucket: RevenueLedgerBucketSchema,
    planCode: RevenueLedgerPlanSchema,
    breakdown: RevenueLedgerBreakdownSchema,
    amountNet: z.number(),
    consumptionTax: z.number(),
    amountInclTax: z.number(),
});
/**
 * 締め後の差のマスを勘定ごとにまとめる（勘定は語彙の順）。付け替えで税抜の合計が 0 になっても、
 * 変わったマスの数で差があったことが分かる。
 *
 * @param diffs 締め後の差のマス
 * @returns 勘定ごとのまとめ
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 締め後に動いたマスを勘定ごとに数え、税抜・税・税込の差を合計する。月の見出しと要対応の詳細に使う。
 * @graph-connects none
 */
export function summarizeClosingDiffs(diffs) {
    return RevenueLedgerBucketSchema.options.flatMap((bucket) => {
        const cells = diffs.filter((d) => d.bucket === bucket);
        if (cells.length === 0)
            return [];
        return [
            {
                bucket,
                cells: cells.length,
                amountNet: cells.reduce((sum, d) => sum + d.amountNet, 0),
                consumptionTax: cells.reduce((sum, d) => sum + d.consumptionTax, 0),
                amountInclTax: cells.reduce((sum, d) => sum + d.amountInclTax, 0),
            },
        ];
    });
}
/**
 * 月の締めの状態1件。diffs は締め後の差のマスの一覧（締め済みで差が無ければ空、締めた計算が台帳に無く
 * 差を出せなければ null、見ている版で締めていなければ空）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business ある月の締めの状態と、確定にした計算・締めた人・勘定ごとの締め後の差額。
 * @graph-connects none
 */
export const RevenueClosingSchema = z.object({
    month: RevenueMonthSchema,
    state: RevenueClosingStateSchema,
    closedVersion: z.string().nullable(),
    closedRunId: z.string().nullable(),
    closedComputedAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    closedBy: z.string().nullable(),
    diffs: z.array(RevenueClosingDiffSchema).nullable(),
});
/**
 * 締めの状態の読み出し条件。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 版と月の範囲を指定して締めの状態を読む条件。
 * @graph-connects none
 */
export const RevenueClosingsQuerySchema = z
    .object({
    version: RevenueClosableVersionSchema,
    fromMonth: RevenueMonthSchema,
    toMonth: RevenueMonthSchema,
})
    .refine((q) => q.fromMonth <= q.toMonth, { message: 'fromMonth must be <= toMonth' });
/**
 * 締めの状態の応答。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 月ごとの締めの状態の一覧を返す応答。
 * @graph-connects none
 */
export const RevenueClosingsResponseSchema = z.object({
    closings: z.array(RevenueClosingSchema),
});
/**
 * 月を締める・締め直す入力。
 * runId は画面の確認モーダルで見せた計算。previousRunId は画面が見ていた確定値（未締めなら null）で、
 * 押した瞬間にこれと違っていたら他の人が先に締めたとみなし、上書きしない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 月を締める・締め直すときに、確定にする計算と、押す前に見ていた確定値を渡す入力。
 * @graph-connects none
 */
export const RevenueCloseInputSchema = z.object({
    version: RevenueClosableVersionSchema,
    month: RevenueMonthSchema,
    runId: z.string().min(1),
    previousRunId: z.string().min(1).nullable(),
});
/**
 * 予実の実績の売上実績の値。正式版（v0.1）の台帳を読み出せなかった期間だけ null（取得失敗。
 * 間違った数字を出さない）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 予実の実績の売上実績1件。取得失敗の期間は null にする。
 * @graph-connects none
 */
export const RevenueActualValueSchema = z.number().nullable();
/**
 * 予実の売上実績（v0.1）の台帳を読み出せなかったときの、売上実績を出さない最初の日と理由。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 予実の売上実績を取得できなかった範囲の始まりと理由。
 * @graph-connects none
 */
export const RevenueActualFailureSchema = z.object({
    from: RevenueDateSchema,
    reason: z.string(),
});
/**
 * 予実の応答に載せる売上実績（v0.1）の状態。
 * - revenueUncomputedFrom: 台帳にまだ無い最初の日（この日以降の売上実績の 0 は未計算。全て計算済みなら null）
 * - revenueFailure: 台帳を読み出せなかったときの範囲と理由（読めたら null）
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 予実の売上実績が未計算か・取得に失敗したかを、API と画面で同じ形で受け渡す。
 * @graph-connects none
 */
export const RevenueActualStatusSchema = z.object({
    revenueUncomputedFrom: RevenueDateSchema.nullable(),
    revenueFailure: RevenueActualFailureSchema.nullable(),
});
//# sourceMappingURL=revenue-ledger.js.map